import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { UNSAFE_NavigationContext, useNavigate } from "react-router-dom";

interface UseUploadNavigationGuardProps {
    uploadCount: number;
    onNavigationAttempt: () => void;
}

/**
 * Hook to guard navigation when uploads are in progress
 * Shows a warning modal when user tries to navigate away with active uploads
 */
export const useUploadNavigationGuard = ({
    uploadCount,
    onNavigationAttempt,
}: UseUploadNavigationGuardProps) => {
    const navigate = useNavigate();
    const navigationContext = useContext(UNSAFE_NavigationContext);
    const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
    const isNavigatingRef = useRef(false);
    const uploadCountRef = useRef(uploadCount);

    // Update ref SYNCHRONOUSLY during render to avoid timing issues
    // This ensures the ref is always up-to-date when navigation happens
    uploadCountRef.current = uploadCount;
    
    // Intercept navigation by wrapping the navigator
    useEffect(() => {
        if (!navigationContext?.navigator) return;

        const originalPush = navigationContext.navigator.push;
        const originalReplace = navigationContext.navigator.replace;

        // Override push method
        navigationContext.navigator.push = function(to: any, state?: any) {
            // Use ref to get current value, check if count > 0
            const shouldBlock = uploadCountRef.current > 0 && !isNavigatingRef.current;
            if (shouldBlock) {
                const path = typeof to === 'string' ? to : to.pathname;
                setPendingNavigation(path);
                onNavigationAttempt();
                return;
            }
            return originalPush.call(this, to, state);
        };

        // Override replace method
        navigationContext.navigator.replace = function(to: any, state?: any) {
            // Use ref to get current value, check if count > 0
            const shouldBlock = uploadCountRef.current > 0 && !isNavigatingRef.current;
            if (shouldBlock) {
                const path = typeof to === 'string' ? to : to.pathname;
                setPendingNavigation(path);
                onNavigationAttempt();
                return;
            }
            return originalReplace.call(this, to, state);
        };

        return () => {
            // Restore original methods
            navigationContext.navigator.push = originalPush;
            navigationContext.navigator.replace = originalReplace;
        };
    }, [onNavigationAttempt, navigationContext]);

    // Handle browser back/forward buttons
    useEffect(() => {
        const handlePopState = (e: PopStateEvent) => {
            // Use ref to get current value
            if (uploadCountRef.current > 0 && !isNavigatingRef.current) {
                e.preventDefault();
                window.history.pushState(null, "", window.location.href);
                onNavigationAttempt();
            }
        };

        window.addEventListener("popstate", handlePopState);
        
        // Only push state if uploads are active
        if (uploadCountRef.current > 0) {
            window.history.pushState(null, "", window.location.href);
        }

        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    }, [onNavigationAttempt]);

    const confirmNavigation = useCallback(() => {
        if (pendingNavigation) {
            isNavigatingRef.current = true;
            // Use setTimeout to ensure ref is updated before navigation
            setTimeout(() => {
                navigate(pendingNavigation);
                setPendingNavigation(null);
                // Reset flag after navigation completes
                setTimeout(() => {
                    isNavigatingRef.current = false;
                }, 200);
            }, 0);
        }
    }, [pendingNavigation, navigate]);

    const cancelNavigation = useCallback(() => {
        setPendingNavigation(null);
    }, []);

    return {
        pendingNavigation,
        confirmNavigation,
        cancelNavigation,
    };
};
